import { Eye, EyeOff } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field } from '@/components/ui/admin/field';
import { date } from '@/lib/date';
import { cn } from '@/lib/utils';

export type ColSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export const smSpan = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
} as const;

export const mdSpan = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
} as const;

export const lgSpan = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
} as const;

/**
 * O grid do formulário (`FormContent`) é `auto-fit` de 280px, não 12 colunas — um `lg`/`md`
 * de 12 vira "ocupa a linha toda" (`col-span-full`); qualquer outro valor fica no tamanho
 * padrão da célula. Mantém a prop por compatibilidade com quem já passa `sm/md/lg`.
 */
export function spanToClassName(sm?: ColSpan, md?: ColSpan, lg?: ColSpan) {
  const full = lg === 12 || md === 12 || sm === 12;
  return full ? 'col-span-full' : undefined;
}

/**
 * Props que todo componente de `form-fields` acaba redeclarando. Componentes
 * novos devem estender esse tipo em vez de repetir name/control/label/sm/md/lg/required/className.
 */
export type CommonFormFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  required?: boolean;
  disabled?: boolean;
  className?: string;
};

type FormInputProps<T extends FieldValues> = CommonFormFieldProps<T> & {
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;

  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
} & React.ComponentProps<'input'>;

/**
 * O cliente Eden converte strings de data da API em `Date` — `<input type="date">` só aceita
 * "YYYY-MM-DD" (e datetime-local "YYYY-MM-DDTHH:mm"), senão fica vazio na edição.
 */
function toInputValue(value: unknown, type: string) {
  if (value === undefined || value === null) return '';
  if (type !== 'date' && type !== 'datetime-local') return value as string;

  const parsed = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);

  // Data pura vem como meia-noite UTC — ler em UTC evita voltar um dia no fuso do Brasil.
  if (type === 'date') return parsed.toISOString().slice(0, 10);
  return date(parsed).format('YYYY-MM-DDTHH:mm');
}

export function FormInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  leftAddon,
  rightAddon,
  sm,
  md,
  lg,
  className,
  required,
  ...props
}: FormInputProps<T>) {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <Field
            {...field}
            {...props}
            id={name}
            // Chrome ignora autocomplete="off" para campos que reconhece como
            // nome/endereço/contato — só o desliga de fato quando o `name` do DOM
            // não bate com os padrões dele (o Controller não depende do `name`
            // nativo pra nada, é só isso aqui em baixo).
            name={`${field.name}-f`}
            label={label}
            required={required}
            invalid={fieldState.invalid}
            value={toInputValue(field.value, type)}
            // `<input type="number">` devolve texto ("50"); a API valida `z.number()`.
            // Vazio vira `null` para dar pra limpar o campo (campos numéricos são nullish).
            onChange={
              type === 'number'
                ? (e: React.ChangeEvent<HTMLInputElement>) =>
                    field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                : field.onChange
            }
            disabled={field.disabled ?? props.disabled}
            placeholder={placeholder}
            type={isPassword ? (showPassword ? 'text' : 'password') : type}
            autoComplete={autoComplete ?? 'off'}
            data-1p-ignore
            data-lpignore="true"
            aria-invalid={fieldState.invalid}
            leading={leftAddon}
            trailing={
              isPassword ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="shrink-0 text-faint hover:text-muted-foreground"
                >
                  {showPassword ? (
                    <Eye className="size-[17px]" />
                  ) : (
                    <EyeOff className="size-[17px]" />
                  )}
                </button>
              ) : (
                rightAddon
              )
            }
          />

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
